import { TerrainSlice, Tile } from './store.d'
import EasyStar from 'easystarjs'
import { produce } from 'immer'
import { Object3D, Vector2 } from 'three'
import { StateCreator } from 'zustand'
import InteractiveTileConfig from '../../e.elements/Grid/scripts/classes/InteractiveTileConfig'
import { TerrainInitialState } from './initialState'
import { GameSceneStoreStype } from '../../a.scenes/GameScene/store'
import { Point } from '../../d.controllers/AnimationController/scripts/characterAnimator'
import { TreasureConfig } from '../../f.assets/Characters/scripts/classes/TreasureConfig'
import { useScenesStore } from '../../b.providers/ScenesProvider/store'
import { COMMANDERS } from '../LevelManager/store.d'

const easystar = new EasyStar.js()

export const createTerrainSlice: StateCreator<GameSceneStoreStype, [['zustand/devtools', never]], [], TerrainSlice> = (
  set,
  get,
) => ({
  terrain: {
    data: {
      ...TerrainInitialState.data,
    },
    resetTerrain: () =>set(
      produce((state: GameSceneStoreStype) => {
        state.terrain.data = TerrainInitialState.data
      }),
      false,
      { type: 'resetTerrain' },
    ),
    initGrid: (initialGridState: InteractiveTileConfig[][]) => {
      set(
        produce(state => {
          /**
           * coords = 0, 0 => point -1, 0, 1
           *
           * x\y---   ^z
           * |1 2 3|  |1 2 3
           * |4 5 6|  |4 5 6
           * |7 8 9|  |7 8 9
           *  -----   x----->
           */
          state.terrain.data.grid = initialGridState
          // Transposing result to match x, y with view in Three scene
          // @TODO: I don't know why removing the trasposing algo the grid works fine anyway
          // looks like the one needing this transposition was easystar, the process was moved to Terrain api getWalkableLandscape
        }),
        false,
        { type: 'initGrid' },
      )
    },
    setTravelPath: (path: Record<'x' | 'y', number>[]) =>
      set(
        produce((state: GameSceneStoreStype) => {
          const sideEffect = path.length > 0 ? state.terrain.lockInteraction : state.terrain.unlockInteraction
          sideEffect()
          state.terrain.data.travelPath = path
        }),
        false,
        { type: 'setTravelPath' },
      ),
    setGridOrigin: (coords: Vector2) =>
      set(
        produce(state => {
          state.terrain.data.gridOrigin = new Vector2(coords.x, coords.y)
        }),
        false,
        { type: 'setGridOrigin' },
      ),
    setSelectedCoords: (coords: Vector2) => {
      set(
        produce(state => {
          const uuidTileCoords = get().characters.getUuidByCoords(coords)
          state.terrain.data.selectedTile = coords
          state.terrain.data.selectedElementUUID = state.terrain.data.selectedElementUUID || uuidTileCoords
        }),
        false,
        { type: 'setSelectedCoords' },
      )
    },
    clearSelectedCoords: () =>
      set(
        produce(state => {
          state.terrain.data.selectedTile = null
          state.terrain.data.selectedElementUUID = null
        }),
        false,
        { type: 'clearSelectedCoords' },
      ),
    lockInteraction: () =>
      set(
        produce(state => {
          state.terrain.data.isLocked = true
        }),
        false,
        { type: 'lockInteraction' },
      ),
    unlockInteraction: () =>
      set(
        produce(state => {
          state.terrain.data.isLocked = false
        }),
        false,
        { type: 'unlockInteraction' },
      ),
    getSelectedState: () => {
      const store = get()
      return store.characters.data.characterStates[store.terrain.data.selectedElementUUID!]
    },
    getWalkableLandscape: () => {
      const grid = useScenesStore.getState().getGameElement('gridElement')?.userData.grid || [[]]
      // easystar needs a transposed grid
      const transGrid = grid[0].map((_: never, c: number) => grid.map((r: any) => r[c]))
      const walkableGrid = transGrid.map((col: Tile[]) =>
        col.map((cell: Tile) => {
          return cell.isWalkable() ? 0 : 1
        }),
      )

      return walkableGrid
    },
    calcPath: (fromX = 0, fromY = 0, toX = 0, toY = 0, callback = () => {}) => {
      const store = get()
      const grid = store.terrain.getWalkableLandscape()
      // const grid = store.terrain.getWalkableLandscape()

      easystar.setGrid(grid)
      easystar.setAcceptableTiles([0])
      try {
        easystar.findPath(fromX, fromY, toX, toY, path => {
          // truncate path to movement allowed value
          callback((path || []).map((p: Point) => new Vector2(p.x, p.y)))
        })
        easystar.calculate()
      } catch (e: any) {
        console.warn(e.message)
        callback([])
      }
    },
    setTreasures: (treasures: TreasureConfig[]) => {
      set(
        produce(state => {
          const dataToSet = treasures.map(t => ({ ...t, amount_max: t.amount_max ?? t.amount }))
          state.terrain.data.treasures = dataToSet
        }),
        false,
        { type: 'setTreasures' },
      )
    },
    updateTreasure: (owner: COMMANDERS, data: Partial<TreasureConfig>) => {
      set(
        produce((state: TerrainSlice) => {
          const treasure = state.terrain.data.treasures.find(t => t.owner === owner)
          if (treasure) {
            Object.assign(treasure, data)
          }
        }),
        false,
        { type: 'updateTreasure' },
      )
    },
    addGravestone: (coords: Vector2) => {
      set(
        produce(state => {
          state.terrain.data.gravestones.push(coords)
        }),
        false,
        { type: 'addGravestone' },
      )
    },
    removeGravestone: (coords: Vector2) => {
      set(
        produce(state => {
          state.terrain.data.gravestones.filter((g: Vector2) => g.equals(coords))
        }),
        false,
        { type: 'removeGravestone' },
      )
    },
  },
})
